/**
 * The four networks a post can go to, Instagram, Facebook, Pinterest and
 * TikTok, each in its own colours, side by side. Decoration beside the Share
 * heading (the section's words name the networks), so hidden from screen
 * readers.
 *
 * The Pinterest and TikTok marks are the networks' own paths (the TikTok one
 * drawn three times, cyan and red offset behind white, as its logo is).
 *
 * Used by: sections/ShareV2.tsx.
 *
 * @module SocialBadges
 */

import React from 'react';

/** TikTok's note mark. */
const TIKTOK_PATH = 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z';

/** Pinterest's P mark. */
const PINTEREST_PATH = 'M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z';

/** One badge's size in pixels. */
const SIZE = 32;

/**
 * The four network badges.
 */
export const SocialBadges: React.FC = () => (
  <div className="flex gap-2" aria-hidden="true">
    <svg width={SIZE} height={SIZE} viewBox="0 0 24 24">
      <defs>
        <radialGradient id="landing-instagram-gradient" cx="30%" cy="107%" r="150%">
          <stop offset="0" stopColor="#FDF497" />
          <stop offset=".05" stopColor="#FDF497" />
          <stop offset=".45" stopColor="#FD5949" />
          <stop offset=".6" stopColor="#D6249F" />
          <stop offset=".9" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect x="1" y="1" width="22" height="22" rx="6" fill="url(#landing-instagram-gradient)" />
      <rect x="5.5" y="5.5" width="13" height="13" rx="4" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="3.2" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="16.2" cy="7.8" r="1" fill="#fff" />
    </svg>
    <svg width={SIZE} height={SIZE} viewBox="0 0 24 24">
      <rect x="1" y="1" width="22" height="22" rx="6" fill="#1877F2" />
      <path d="M16 23v-8.2h2.6l.4-3H16V9.9c0-.9.3-1.5 1.5-1.5H19V5.7c-.3 0-1.2-.1-2.2-.1-2.2 0-3.7 1.3-3.7 3.8v2.3H10.5v3H13V23z" fill="#fff" />
    </svg>
    <svg width={SIZE} height={SIZE} viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="12" fill="#fff" />
      <path d={PINTEREST_PATH} fill="#E60023" />
    </svg>
    <svg width={SIZE} height={SIZE} viewBox="0 0 24 24">
      <rect x="0" y="0" width="24" height="24" rx="6" fill="#010101" />
      <g transform="translate(4.6 4.2) scale(.62)">
        <path d={TIKTOK_PATH} fill="#25F4EE" transform="translate(-.9 -.7)" />
        <path d={TIKTOK_PATH} fill="#FE2C55" transform="translate(.9 .7)" />
        <path d={TIKTOK_PATH} fill="#fff" />
      </g>
    </svg>
  </div>
);
